import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UsersHelper, { userError } from '../../helpers/UsersHelper';
import PasswordFields, { passwordError } from './PasswordFields';

const emptyUser = { username: '', email: '', campuses: '', canControl: false, canViewLive: false,
  canGeneratePublicLinks: false, canDownload: false, canManageUsers: false, isActive: true,
  viewPastDaysLimit: '', titleKeywordExcludeFilter: '' };
const permissions = [['canControl', 'Control encoders and recordings'], ['canViewLive', 'View live streams'],
  ['canGeneratePublicLinks', 'Create public links'], ['canDownload', 'Download recordings'],
  ['canManageUsers', 'Manage users'], ['isActive', 'Account active']];

export default function Users() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [signedOut, setSignedOut] = useState(false);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const actor = await UsersHelper.me();
        if (!active) return;
        setMe(actor);
        if (actor.canManageUsers) {
          const rows = await UsersHelper.list();
          if (active) setUsers(rows);
        }
      } catch (err) { if (active) setError(userError(err)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);
  function close() { setEditor(null); setPasswordTarget(null); setPassword(''); setConfirmation(''); }
  function edit(user) {
    close(); setError(''); setNotice('');
    setEditor(user ? { ...user, email: user.email || '', campuses: user.campuses || '',
      viewPastDaysLimit: user.viewPastDaysLimit === null ? '' : user.viewPastDaysLimit,
      titleKeywordExcludeFilter: user.titleKeywordExcludeFilter || '' } : { ...emptyUser });
  }
  function field(name, value) { setEditor({ ...editor, [name]: value }); }
  async function refresh() {
    setError('');
    try { setUsers(await UsersHelper.list()); }
    catch (err) { setError(userError(err)); }
  }
  async function save(e) {
    e.preventDefault();
    if (passwordTarget || !editor.id) {
      const validation = passwordError(password, confirmation);
      if (validation) return setError(validation);
    }
    setBusy(true); setError(''); setNotice('');
    try {
      if (passwordTarget) {
        await UsersHelper.setPassword(passwordTarget.id, { newPassword: password, version: passwordTarget.version });
        setNotice('Password changed. Existing sessions for this user have been signed out.');
      } else {
        const data = { ...editor, viewPastDaysLimit: editor.viewPastDaysLimit === '' ? null : Number(editor.viewPastDaysLimit) };
        if (editor.id) await UsersHelper.update(editor.id, data);
        else await UsersHelper.create({ ...data, password });
        if (editor.id === me.id) {
          localStorage.removeItem('token'); close(); setSignedOut(true); return;
        }
        setNotice(editor.id ? 'Account updated. The user must sign in again.' : 'Account created. Share the initial password with the user directly.');
      }
      close(); await refresh();
    } catch (err) { setError(userError(err)); }
    finally { setBusy(false); }
  }
  if (signedOut) return <section className="section"><div className="container"><p role="status">Your account was updated. <Link to="/login">Sign in again</Link> to use the new settings.</p></div></section>;
  if (loading) return <section className="section"><p role="status">Loading users…</p></section>;
  if (!me || !me.canManageUsers) return <section className="section"><div className="container"><h1 className="title">Users</h1><p role="alert">{error || 'You do not have permission to manage users.'}</p></div></section>;
  const editingSelf = editor && editor.id === me.id;
  const filtered = users.filter(u => `${u.username} ${u.email || ''}`.toLowerCase().includes(search.toLowerCase()));
  return <section className="section"><div className="container">
    <h1 className="title">Users</h1>
    <p className="subtitle">Manage accounts, access, and passwords.</p>
    {error && <div className="notification is-danger" role="alert">{error}</div>}
    {notice && <div className="notification is-success" role="status">{notice}</div>}
    {(editor || passwordTarget) ? <form className="box" onSubmit={save} style={{ maxWidth: 720 }}>
      <h2 className="title is-4">{passwordTarget ? `Set password for ${passwordTarget.username}` : editor.id ? `Edit ${editor.username}` : 'Add user'}</h2>
      <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
        {!passwordTarget && <React.Fragment>
          <div className="field"><label className="label" htmlFor="user-username">Username</label>
            <input className="input" id="user-username" autoComplete="off" required maxLength={75} pattern="[^\s]+"
              value={editor.username} onChange={e => field('username', e.target.value)} /></div>
          <div className="field"><label className="label" htmlFor="user-email">Email</label>
            <input className="input" id="user-email" type="email" maxLength={255} value={editor.email} onChange={e => field('email', e.target.value)} />
            <p className="help">Used for download links. Password reset emails are not sent.</p></div>
          <div className="field"><label className="label" htmlFor="user-campuses">Campuses</label>
            <input className="input" id="user-campuses" maxLength={255} value={editor.campuses} onChange={e => field('campuses', e.target.value)} />
            <p className="help">Comma-separated campus names, * for all campuses, or blank for no campus access.</p></div>
          <div className="field"><label className="label" htmlFor="user-days">Recording history limit (days)</label>
            <input className="input" id="user-days" type="number" min="0" max="2147483647" step="1" value={editor.viewPastDaysLimit} onChange={e => field('viewPastDaysLimit', e.target.value)} />
            <p className="help">Leave blank for no limit.</p></div>
          <div className="field"><label className="label" htmlFor="user-exclusions">Excluded title keywords</label>
            <textarea className="textarea" id="user-exclusions" maxLength={65535} value={editor.titleKeywordExcludeFilter} onChange={e => field('titleKeywordExcludeFilter', e.target.value)} />
            <p className="help">Comma-separated keywords used to hide recordings.</p></div>
          <div className="field"><span className="label">Permissions and status</span>
            {permissions.map(([key, label]) => <div key={key}><label className="checkbox">
              <input type="checkbox" checked={editor[key]} disabled={editingSelf && (key === 'isActive' || key === 'canManageUsers')}
                onChange={e => field(key, e.target.checked)} /> {label}</label></div>)}
            <p className="help">Disabling an account blocks sign-in without deleting its history. Account changes sign out existing sessions.</p>
            {editingSelf && <p className="help">You cannot disable yourself or remove your own management permission.</p>}
          </div>
        </React.Fragment>}
        {(passwordTarget || !editor.id) && <PasswordFields password={password} confirmation={confirmation} onPassword={setPassword} onConfirmation={setConfirmation} />}
        <div className="buttons">
          <button type="submit" className={`button is-primary ${busy ? 'is-loading' : ''}`}>{passwordTarget ? 'Set password' : 'Save user'}</button>
          <button type="button" className="button" onClick={() => { close(); setError(''); }}>Cancel</button>
        </div>
      </fieldset>
    </form> : <React.Fragment>
      <div className="buttons"><button className="button is-primary" onClick={() => edit(null)}>Add user</button><button className="button" onClick={refresh}>Refresh</button></div>
      <div className="field"><label className="label" htmlFor="user-search">Search users</label><input id="user-search" className="input" type="search" value={search} onChange={e => setSearch(e.target.value)} /></div>
      <div className="table-container"><table className="table is-fullwidth is-striped">
        <thead><tr><th>Username</th><th>Email</th><th>Campuses</th><th>Status</th><th>Manage users</th><th>Actions</th></tr></thead>
        <tbody>{filtered.map(user => <tr key={user.id}><td>{user.username}{user.id === me.id ? ' (you)' : ''}</td><td>{user.email || '—'}</td><td>{user.campuses || 'None'}</td><td>{user.isActive ? 'Active' : 'Disabled'}</td><td>{user.canManageUsers ? 'Yes' : 'No'}</td>
          <td><div className="buttons"><button className="button is-small" onClick={() => edit(user)}>Edit<span className="is-sr-only"> {user.username}</span></button>
            {user.id === me.id ? <Link className="button is-small" to="/account/password">Change password</Link> : <button className="button is-small" onClick={() => { close(); setPasswordTarget(user); setError(''); setNotice(''); }}>Set password<span className="is-sr-only"> for {user.username}</span></button>}</div></td></tr>)}</tbody>
      </table>{filtered.length === 0 && <p>No users found.</p>}</div>
    </React.Fragment>}
  </div></section>;
}
